import { isAlpha, isAlphaNum, isDigit } from '../../../../utils/text.util';

import { SyntaxKind } from './SyntaxKind';
import { Token } from './Token';

const SingleCharTokenMap: Record<string, SyntaxKind> = {
  '(': SyntaxKind.LeftParen,
  ')': SyntaxKind.RightParen,
  ':': SyntaxKind.Colon,
  ',': SyntaxKind.Comma,
};

const SingleCharTokens = Object.keys(SingleCharTokenMap);

const SpecialChars = ['=', '>', '<', '!'];

// the order here *matters*!!
// we want first to try the longest one, and then the shorter ones
const MergedTokens = new Map<SyntaxKind, SyntaxKind[]>([
  [
    SyntaxKind.IsNotEmpty,
    [SyntaxKind.Is, SyntaxKind.WhiteSpace, SyntaxKind.Not, SyntaxKind.WhiteSpace, SyntaxKind.Empty],
  ],
  [SyntaxKind.NotContains, [SyntaxKind.Not, SyntaxKind.WhiteSpace, SyntaxKind.Contains]],
  [SyntaxKind.IsNot, [SyntaxKind.Is, SyntaxKind.WhiteSpace, SyntaxKind.Not]],
  [SyntaxKind.IsEmpty, [SyntaxKind.Is, SyntaxKind.WhiteSpace, SyntaxKind.Empty]],
]);

export function scan(code: string): Token[] {
  const tokens: Token[] = [];
  const codeLength = code.length;
  let i = 0;

  function isSpecialOperatorChar(c: string) {
    return SpecialChars.includes(c);
  }

  function scanWhiteSpace() {
    const start = i;

    while (i < codeLength && code[i] === ' ') {
      ++i;
    }

    tokens.push({
      syntaxKind: SyntaxKind.WhiteSpace,
      begin: start,
      end: i,
      value: code.substring(start, i),
    });
  }

  function scanIdentifier() {
    const start = i;

    // we check in the outer loop, before we run this function
    // is the first character isAlpha, so we can just check here
    // if they are all alphanum, otherwise we would need to
    // make a special case for first character
    // (first character cannot be a digit)
    while (i < codeLength && (isAlphaNum(code[i]) || code[i] === '_' || code[i] === '.' || code[i] === '-')) {
      ++i;
    }

    const value = code.substring(start, i);

    let syntaxKind = SyntaxKind.Identifier;

    if (value === 'or' || value === 'OR') {
      syntaxKind = SyntaxKind.Or;
    } else if (value === 'and' || value === 'AND') {
      syntaxKind = SyntaxKind.And;
    } else if (value === 'contains' || value === 'CONTAINS') {
      syntaxKind = SyntaxKind.Contains;
    } else if (value === 'is' || value === 'IS') {
      syntaxKind = SyntaxKind.Is;
    } else if (value === 'not' || value === 'NOT') {
      syntaxKind = SyntaxKind.Not;
    } else if (value === 'empty' || value === 'EMPTY') {
      syntaxKind = SyntaxKind.Empty;
    } else if (value === 'search' || value === 'SEARCH') {
      syntaxKind = SyntaxKind.Search;
    }

    tokens.push({
      syntaxKind,
      begin: start,
      end: i,
      value,
    });
  }

  function scanString() {
    const start = i;
    let isStringClosed = false;

    // we already checked that first character is '"'
    ++i;

    while (i < codeLength) {
      // allow the '\"' special character
      if (code[i] === '"' && code[i - 1] !== '\\') {
        isStringClosed = true;
        ++i;
        break;
      }

      ++i;
    }

    tokens.push({
      syntaxKind: isStringClosed ? SyntaxKind.String : SyntaxKind.Error,
      begin: start,
      end: i,
      value: code.substring(start, i),
    });
  }

  function scanSpecialOperator() {
    const start = i;

    while (i < codeLength && isSpecialOperatorChar(code[i])) {
      ++i;
    }

    const value = code.substring(start, i);

    let syntaxKind = SyntaxKind.Identifier;

    if (value === '=') {
      syntaxKind = SyntaxKind.Equals;
    } else if (value === '!=') {
      syntaxKind = SyntaxKind.NotEquals;
    } else if (value === '>') {
      syntaxKind = SyntaxKind.MoreThan;
    } else if (value === '>=') {
      syntaxKind = SyntaxKind.MoreOrEqualThan;
    } else if (value === '<') {
      syntaxKind = SyntaxKind.LessThan;
    } else if (value === '<=') {
      syntaxKind = SyntaxKind.LessOrEqualThan;
    } else {
      syntaxKind = SyntaxKind.Error;
    }

    tokens.push({
      syntaxKind,
      begin: start,
      end: i,
      value,
    });
  }

  function scanNumber() {
    const start = i;
    let hasDot = false;
    let hasManyDotsError = false;
    let isLastCharacterADot = false;
    let hasInvalidCharacter = false;
    let hasAnyDigit = false;

    if (code[i] === '-') {
      ++i;
    }

    while (i < codeLength && (isAlphaNum(code[i]) || code[i] === '.')) {
      if (code[i] === '.' && !hasDot) {
        hasDot = true;
      } else if (code[i] === '.' && hasDot) {
        hasManyDotsError = true;
      }

      if (code[i] === '.') {
        isLastCharacterADot = true;
      } else {
        isLastCharacterADot = false;
      }

      const isDigitChar = isDigit(code[i]);

      if (!isDigitChar && code[i] !== '.') {
        hasInvalidCharacter = true;
      }

      if (isDigitChar) {
        hasAnyDigit = true;
      }

      ++i;
    }

    const value = code.substring(start, i);
    let syntaxKind = SyntaxKind.Integer;

    if (hasDot) {
      syntaxKind = SyntaxKind.Float;
    }

    if (hasManyDotsError || isLastCharacterADot || hasInvalidCharacter || !hasAnyDigit) {
      syntaxKind = SyntaxKind.Error;
    }

    tokens.push({
      syntaxKind,
      begin: start,
      end: i,
      value,
    });
  }

  while (i < codeLength) {
    const c = code[i];

    if (SingleCharTokens.includes(c)) {
      tokens.push({
        syntaxKind: SingleCharTokenMap[c],
        begin: i,
        end: i + 1,
        value: c,
      });
      ++i;
    } else if (c === ' ') {
      scanWhiteSpace();
    } else if (isAlpha(c)) {
      scanIdentifier();
    } else if (c === '"') {
      scanString();
    } else if (isSpecialOperatorChar(c)) {
      scanSpecialOperator();
    } else if (isDigit(c) || c === '-') {
      scanNumber();
    } else {
      if (tokens.length > 0 && tokens[tokens.length - 1].syntaxKind === SyntaxKind.Error) {
        // if we are already in error mode, just add this character
        ++tokens[tokens.length - 1].end;
        tokens[tokens.length - 1].value = code.substring(
          tokens[tokens.length - 1].begin,
          tokens[tokens.length - 1].end,
        );
      } else {
        // if this is the first encounter of the error, push it to the tokens array
        tokens.push({
          syntaxKind: SyntaxKind.Error,
          begin: i,
          end: i + 1,
          value: code.substring(i, i + 1),
        });
      }

      ++i;
    }
  }

  tokens.push({
    syntaxKind: SyntaxKind.EOF,
    begin: codeLength,
    end: codeLength,
    value: '',
  });

  function isSubArray(superArray: Token[], subArray: SyntaxKind[], superArrayStart: number) {
    let _isSubArray = true;

    for (let j = 0; j < subArray.length; ++j) {
      if (!(superArray[superArrayStart + j] && superArray[superArrayStart + j].syntaxKind === subArray[j])) {
        _isSubArray = false;
        break;
      }
    }

    return _isSubArray;
  }

  // because we have merging tokens like "is not empty" or "not contains"
  // we need to do a second pass through them and merge those which are
  // necessary to be merged
  // we are also merging tokens that are invalid in the final language
  // like "is not", because it will help code completion to properly
  // target the parts of the string that needs to be modified
  const finalTokens: Token[] = [];

  for (let t = 0; t < tokens.length; ++t) {
    let matched = false;

    for (const mergedTokenKey of MergedTokens.keys()) {
      const subArray = MergedTokens.get(mergedTokenKey)!;

      if (isSubArray(tokens, subArray, t)) {
        matched = true;

        const begin = tokens[t].begin;
        const end = tokens[t + subArray.length - 1].end;

        finalTokens.push({
          syntaxKind: mergedTokenKey,
          begin,
          end,
          value: code.substring(begin, end),
        });
        t += subArray.length - 1;
        break;
      }
    }

    if (!matched) {
      finalTokens.push(tokens[t]);
    }
  }

  return finalTokens;
}
